import React, { useState } from 'react';
import { ChevronRightIcon } from '../icons/ChevronRightIcon';
import { ChevronLeftIcon } from '../icons/ChevronLeftIcon';
import { TrashIcon } from '../icons/TrashIcon';

const FONT_OPTIONS = ['sans-serif', 'serif', 'monospace', 'cursive', 'fantasy'];
const TRANSITION_TYPES = ['none', 'fade-in', 'fade-out', 'cross-dissolve'];

export const InspectorPanel = ({ selectedClip, project, onUpdateClip, onDeleteClip, isCollapsed, setIsCollapsed }) => {
    if (isCollapsed) {
        return (
            <div className="bg-[#1f1f29] border-l border-gray-700 flex flex-col p-2 items-center shrink-0">
                <button 
                    onClick={() => setIsCollapsed(false)} 
                    className="p-2 rounded-md hover:bg-gray-700 transition-colors"
                    aria-label="Expand Inspector Panel"
                >
                    <ChevronLeftIcon className="w-5 h-5" />
                </button>
            </div>
        );
    }

    if (!selectedClip) {
        return (
            <div className="w-[300px] bg-[#1f1f29] border-l border-gray-700 flex flex-col p-4 overflow-y-auto shrink-0 relative">
                 <button 
                    onClick={() => setIsCollapsed(true)} 
                    className="absolute top-2 right-2 p-2 rounded-md hover:bg-gray-700 transition-colors z-10"
                    aria-label="Collapse Inspector Panel"
                 >
                    <ChevronRightIcon className="w-5 h-5" />
                 </button>
                 <h2 className="text-xl font-bold mb-4">Properties</h2>
                <div className="flex-grow flex items-center justify-center text-gray-500 text-sm">
                    <p>Select a clip to edit its properties</p>
                </div>
            </div>
        );
    }

    const handleUpdate = (updates) => {
        onUpdateClip({ ...selectedClip, ...updates });
    };

    return (
        <div className="w-[300px] bg-[#1f1f29] border-l border-gray-700 flex flex-col p-4 space-y-4 overflow-y-auto shrink-0 relative">
             <button 
                onClick={() => setIsCollapsed(true)} 
                className="absolute top-2 right-2 p-2 rounded-md hover:bg-gray-700 transition-colors z-10"
                aria-label="Collapse Inspector Panel"
             >
                <ChevronRightIcon className="w-5 h-5" />
             </button>
             <h2 className="text-xl font-bold">Properties</h2>

            {/* Common Properties */}
            <div className="space-y-3">
                <div>
                    <label className="text-xs text-gray-400 block mb-1">Start Time (s)</label>
                    <input
                        type="number"
                        step="0.1"
                        value={selectedClip.start.toFixed(2)}
                        onChange={(e) => handleUpdate({ start: parseFloat(e.target.value) || 0 })}
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-sm"
                    />
                </div>
                <div>
                    <label className="text-xs text-gray-400 block mb-1">Duration (s)</label>
                    <input
                        type="number"
                        step="0.1"
                        value={selectedClip.duration.toFixed(2)}
                        onChange={(e) => handleUpdate({ duration: Math.max(0.1, parseFloat(e.target.value) || 0.1) })}
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-sm"
                    />
                </div>
            </div>

            {/* Type-specific properties */}
            {selectedClip.type === 'text' && (
                <div className="space-y-3 pt-3 border-t border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-300">Text Properties</h3>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Content</label>
                        <textarea
                            value={selectedClip.content || ''}
                            onChange={(e) => handleUpdate({ content: e.target.value })}
                            rows={3}
                            className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-sm resize-none"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Font Size</label>
                        <input
                            type="number"
                            value={selectedClip.fontSize || 48}
                            onChange={(e) => handleUpdate({ fontSize: parseInt(e.target.value) || 48 })}
                            className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-sm"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Font Family</label>
                        <select
                            value={selectedClip.fontFamily || 'sans-serif'}
                            onChange={(e) => handleUpdate({ fontFamily: e.target.value })}
                            className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-sm"
                        >
                            {FONT_OPTIONS.map(font => (
                                <option key={font} value={font}>{font}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Color</label>
                        <input
                            type="color"
                            value={selectedClip.color || '#ffffff'}
                            onChange={(e) => handleUpdate({ color: e.target.value })}
                            className="w-full h-10 bg-gray-700 border border-gray-600 rounded-md"
                        />
                    </div>
                </div>
            )}

            {selectedClip.type === 'audio' && (
                <div className="space-y-3 pt-3 border-t border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-300">Audio Properties</h3>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Volume</label>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={selectedClip.volume || 1}
                            onChange={(e) => handleUpdate({ volume: parseFloat(e.target.value) })}
                            className="w-full"
                        />
                        <span className="text-xs text-gray-500">{Math.round((selectedClip.volume || 1) * 100)}%</span>
                    </div>
                </div>
            )}

            {selectedClip.type === 'video' && (
                <div className="space-y-3 pt-3 border-t border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-300">Video Properties</h3>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Speed</label>
                        <select
                            value={selectedClip.speed || 1}
                            onChange={(e) => handleUpdate({ speed: parseFloat(e.target.value) })}
                            className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-sm"
                        >
                            <option value={0.5}>0.5x (Slow)</option>
                            <option value={1}>1.0x (Normal)</option>
                            <option value={1.5}>1.5x</option>
                            <option value={2}>2.0x (Fast)</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Transition</label>
                        <select
                            value={selectedClip.transition?.type || 'none'}
                            onChange={(e) => handleUpdate({ 
                                transition: { type: e.target.value, duration: selectedClip.transition?.duration || 0.5 } 
                            })}
                            className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-sm"
                        >
                            {TRANSITION_TYPES.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                    {selectedClip.transition?.type !== 'none' && (
                        <div>
                            <label className="text-xs text-gray-400 block mb-1">Transition Duration (s)</label>
                            <input
                                type="number"
                                step="0.1"
                                value={selectedClip.transition?.duration || 0.5}
                                onChange={(e) => handleUpdate({ 
                                    transition: { ...selectedClip.transition, duration: parseFloat(e.target.value) || 0.5 } 
                                })}
                                className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-sm"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Delete Button */}
            <button
                onClick={() => onDeleteClip(selectedClip.id)}
                className="w-full mt-auto bg-red-600/20 hover:bg-red-600/40 text-red-400 font-semibold py-2 px-4 rounded-lg flex items-center justify-center transition-colors"
            >
                <TrashIcon className="w-5 h-5 mr-2" />
                Delete Clip
            </button>
        </div>
    );
};
